import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import StorageIcon from '@mui/icons-material/Storage';
import QueueIcon from '@mui/icons-material/Queue';
import SearchIcon from '@mui/icons-material/Search';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useMe } from '../api/hooks.js';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface Config {
  storage: { driver: string; localPath: string; bucket: string; endpoint: string; region: string; forcePathStyle: string; accessKeyId: string; hasSecret: boolean };
  queue: { driver: string; redisKey: string; sqsUrl: string; redisUrl: string };
  search: { enabled: boolean; node: string; index: string };
  persistedKeys: string[];
  restartPending: boolean;
}

export function ConfigurationPage() {
  const t = useT().configuration;
  const { data: me } = useMe();
  const orgId = me?.organizations[0]?.id;
  const isAdmin = ['OWNER', 'ADMIN'].includes(me?.organizations[0]?.role ?? '');
  const base = `/v1/orgs/${orgId}/deployment/config`;
  const qc = useQueryClient();

  const cfg = useQuery({
    enabled: !!orgId && isAdmin,
    queryKey: ['deployment-config', orgId],
    queryFn: async () => (await api.get(base)).data as Config,
  });

  const EMPTY_FORM = {
    storageDriver: 's3', storageLocalPath: '', storageBucket: '', storageEndpoint: '',
    storageRegion: '', storageForcePathStyle: 'auto', storageAccessKeyId: '', storageSecretAccessKey: '',
    queueDriver: 'sqs', queueRedisKey: '', queueSqsUrl: '', queueRedisUrl: '',
    searchEnabled: true, searchNode: '', searchIndex: '',
  };
  // Local form state, seeded from the server once loaded. `saved` keeps the last
  // persisted snapshot so we can detect unsaved edits and offer a discard.
  const [preset, setPreset] = useState('other');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saved, setSaved] = useState(EMPTY_FORM);

  // Build the form snapshot from a server config and load it into both form + saved.
  const hydrate = (data: Config) => {
    const next = {
      ...EMPTY_FORM,
      storageDriver: data.storage.driver,
      storageLocalPath: data.storage.localPath,
      storageBucket: data.storage.bucket,
      storageEndpoint: data.storage.endpoint,
      storageRegion: data.storage.region,
      storageForcePathStyle: data.storage.forcePathStyle,
      storageAccessKeyId: '', // write-only; current value is shown as a placeholder
      storageSecretAccessKey: '',
      queueDriver: data.queue.driver,
      queueRedisKey: data.queue.redisKey,
      queueSqsUrl: data.queue.sqsUrl,
      queueRedisUrl: data.queue.redisUrl,
      searchEnabled: data.search.enabled,
      searchNode: data.search.node,
      searchIndex: data.search.index,
    };
    setForm(next);
    setSaved(next);
    // Infer the provider preset from the running config (can't tell MinIO/Ceph apart).
    setPreset(data.storage.driver === 's3' && data.storage.endpoint ? 'other' : 'aws');
  };
  useEffect(() => {
    if (cfg.data) hydrate(cfg.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.data]);

  // Are there edits that differ from the last saved snapshot?
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  // Have settings already been written to the DB? Drives Save vs Update wording.
  const hasPersisted = (cfg.data?.persistedKeys.length ?? 0) > 0;

  // Provider presets pre-fill the fields that differ between S3-compatible stores.
  const applyPreset = (p: string) => {
    setPreset(p);
    setForm((f) => {
      if (p === 'aws') return { ...f, storageEndpoint: '', storageForcePathStyle: 'auto', storageRegion: f.storageRegion || 'us-east-1' };
      if (p === 'minio') return { ...f, storageForcePathStyle: 'true', storageRegion: f.storageRegion || 'us-east-1', storageEndpoint: f.storageEndpoint || 'http://minio:9000' };
      if (p === 'ceph') return { ...f, storageForcePathStyle: 'true', storageRegion: f.storageRegion || 'us-east-1' };
      return f;
    });
  };

  const save = useMutation({
    mutationFn: async () => (await api.put(base, form)).data as Config,
    onSuccess: (data) => {
      hydrate(data); // clears dirty + wipes the write-only secret fields
      qc.setQueryData(['deployment-config', orgId], data);
    },
  });

  // The fields that belong to each connection section — used to tell whether a
  // section changed and whether its current values passed a Test connection.
  const SECTION_FIELDS = {
    storage: ['storageDriver', 'storageLocalPath', 'storageBucket', 'storageEndpoint', 'storageRegion', 'storageForcePathStyle', 'storageAccessKeyId', 'storageSecretAccessKey'],
    queue: ['queueDriver', 'queueRedisKey', 'queueSqsUrl', 'queueRedisUrl'],
    search: ['searchEnabled', 'searchNode', 'searchIndex'],
  } as const;
  type Target = keyof typeof SECTION_FIELDS;
  const sectionSig = (t: Target, f: typeof form) => JSON.stringify(SECTION_FIELDS[t].map((k) => f[k as keyof typeof form]));
  const sectionChanged = (t: Target) => sectionSig(t, form) !== sectionSig(t, saved);

  // Connectivity tests — probe each backend with the current (unsaved) values.
  const testBase = `/v1/orgs/${orgId}/deployment/test-connection`;
  const [testing, setTesting] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, { ok: boolean; message: string }>>({});
  // Signature of a section's values at the moment its Test connection last passed.
  const [verified, setVerified] = useState<Record<string, string>>({});
  const runTest = async (target: Target) => {
    setTesting(target);
    setResults((r) => { const n = { ...r }; delete n[target]; return n; });
    try {
      const res = (await api.post(testBase, { ...form, target })).data as { ok: boolean; message: string };
      setResults((r) => ({ ...r, [target]: res }));
      // Remember exactly which values passed, so a later edit re-flags the section.
      if (res.ok) setVerified((v) => ({ ...v, [target]: sectionSig(target, form) }));
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? t.testFailed;
      setResults((r) => ({ ...r, [target]: { ok: false, message: msg } }));
    } finally {
      setTesting(null);
    }
  };

  // Changed sections whose current values haven't passed a Test connection.
  const relevantTargets = (): Target[] => ['storage', 'queue', ...(form.searchEnabled ? (['search'] as const) : [])];
  const unverifiedChanged = (): Target[] =>
    relevantTargets().filter((t) => sectionChanged(t) && verified[t] !== sectionSig(t, form));

  const [confirm, setConfirm] = useState<Target[] | null>(null);
  const attemptSave = () => {
    const pending = unverifiedChanged();
    if (pending.length) setConfirm(pending); // warn before persisting an untested config
    else save.mutate();
  };

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  if (!isAdmin) {
    return (
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>{t.title}</Typography>
        <Alert severity="info">{t.needAdmin}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <TuneIcon color="primary" />
        <Typography variant="h5">{t.title}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t.subtitle}
      </Typography>

      {cfg.isLoading && <LinearProgress sx={{ mb: 2 }} />}
      {cfg.data?.restartPending && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t.restartPending}
        </Alert>
      )}

      {/* Storage */}
      <Section icon={<StorageIcon />} title={t.storage.title} subtitle={t.storage.subtitle}>
        <TextField select fullWidth label={t.storage.driver} value={form.storageDriver} onChange={(e) => set('storageDriver', e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="s3">{t.storage.s3}</MenuItem>
          <MenuItem value="local">{t.storage.local}</MenuItem>
        </TextField>
        {form.storageDriver === 's3' ? (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField select fullWidth label={t.storage.preset} value={preset} onChange={(e) => applyPreset(e.target.value)} helperText={t.storage.presetHelp}>
                <MenuItem value="aws">{t.storage.presetAws}</MenuItem>
                <MenuItem value="minio">{t.storage.presetMinio}</MenuItem>
                <MenuItem value="ceph">{t.storage.presetCeph}</MenuItem>
                <MenuItem value="other">{t.storage.presetOther}</MenuItem>
              </TextField>
              <TextField select label={t.storage.pathStyle} value={form.storageForcePathStyle} onChange={(e) => set('storageForcePathStyle', e.target.value)} sx={{ minWidth: 210 }} helperText={t.storage.pathStyleHelp}>
                <MenuItem value="auto">{t.storage.pathAuto}</MenuItem>
                <MenuItem value="true">{t.storage.pathOn}</MenuItem>
                <MenuItem value="false">{t.storage.pathOff}</MenuItem>
              </TextField>
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField fullWidth label={t.storage.bucket} value={form.storageBucket} onChange={(e) => set('storageBucket', e.target.value)} />
              <TextField label={t.storage.region} value={form.storageRegion} onChange={(e) => set('storageRegion', e.target.value)} sx={{ minWidth: 160 }} />
            </Stack>
            <TextField fullWidth label={preset === 'aws' ? t.storage.endpointAws : t.storage.endpoint} placeholder="http://minio:9000" value={form.storageEndpoint} onChange={(e) => set('storageEndpoint', e.target.value)} helperText={t.storage.endpointHelp} />
            <TextField
              fullWidth
              label={t.storage.accessKey}
              value={form.storageAccessKeyId}
              onChange={(e) => set('storageAccessKeyId', e.target.value)}
              placeholder={cfg.data?.storage.accessKeyId || 'AKIA…'}
              helperText={t.storage.accessKeyHelp}
            />
            <TextField
              fullWidth
              type="password"
              label={t.storage.secretKey}
              value={form.storageSecretAccessKey}
              onChange={(e) => set('storageSecretAccessKey', e.target.value)}
              placeholder={cfg.data?.storage.hasSecret ? '•••••••• (configured)' : 'enter secret'}
              helperText={t.storage.secretKeyHelp}
            />
          </Stack>
        ) : (
          <TextField fullWidth label={t.storage.directory} value={form.storageLocalPath} onChange={(e) => set('storageLocalPath', e.target.value)} helperText={t.storage.directoryHelp} />
        )}
        <TestRow target="storage" testing={testing} result={results.storage} onTest={() => runTest('storage')} />
      </Section>

      {/* Queue */}
      <Section icon={<QueueIcon />} title={t.queue.title} subtitle={t.queue.subtitle}>
        <TextField select fullWidth label={t.queue.driver} value={form.queueDriver} onChange={(e) => set('queueDriver', e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="sqs">{t.queue.sqs}</MenuItem>
          <MenuItem value="redis">{t.queue.redis}</MenuItem>
        </TextField>
        {form.queueDriver === 'sqs' ? (
          <TextField fullWidth label={t.queue.sqsUrl} value={form.queueSqsUrl} onChange={(e) => set('queueSqsUrl', e.target.value)} placeholder="https://sqs.us-east-1.amazonaws.com/…/replayops-ingest" helperText={t.queue.sqsHelp} />
        ) : (
          <Stack spacing={2}>
            <TextField fullWidth label={t.queue.redisUrl} value={form.queueRedisUrl} onChange={(e) => set('queueRedisUrl', e.target.value)} placeholder="redis://redis:6379" helperText={t.queue.redisHelp} />
            <TextField fullWidth label={t.queue.redisKey} value={form.queueRedisKey} onChange={(e) => set('queueRedisKey', e.target.value)} />
          </Stack>
        )}
        <TestRow target="queue" testing={testing} result={results.queue} onTest={() => runTest('queue')} />
      </Section>

      {/* Search */}
      <Section icon={<SearchIcon />} title={t.search.title} subtitle={t.search.subtitle}>
        <FormControlLabel
          control={<Switch checked={form.searchEnabled} onChange={(e) => set('searchEnabled', e.target.checked)} />}
          label={t.search.enabled}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: form.searchEnabled ? 2 : 0 }}>
          Turn off for air-gapped installs without OpenSearch. Search and funnels fall back to Postgres.
        </Typography>
        {form.searchEnabled && (
          <Stack direction="row" spacing={2}>
            <TextField fullWidth label={t.search.node} value={form.searchNode} onChange={(e) => set('searchNode', e.target.value)} placeholder="http://opensearch:9200" />
            <TextField label={t.search.index} value={form.searchIndex} onChange={(e) => set('searchIndex', e.target.value)} sx={{ minWidth: 180 }} />
          </Stack>
        )}
        {form.searchEnabled && <TestRow target="search" testing={testing} result={results.search} onTest={() => runTest('search')} />}
      </Section>

      {/* Sticky action bar. Always visible so the update action is never buried. */}
      <Paper
        elevation={3}
        sx={{
          position: 'sticky', bottom: 16, mt: 2, p: 1.75, borderRadius: 2,
          border: '1px solid', borderColor: dirty ? 'primary.main' : 'divider',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', zIndex: 2,
        }}
      >
        <Button
          variant="contained"
          disabled={save.isPending || !dirty}
          onClick={attemptSave}
        >
          {save.isPending ? t.actions.saving : hasPersisted ? t.actions.update : t.actions.save}
        </Button>
        <Button variant="text" color="inherit" disabled={!dirty || save.isPending} onClick={() => cfg.data && hydrate(cfg.data)}>
          {t.actions.discard}
        </Button>

        <Box sx={{ flex: 1 }} />

        {save.isError ? (
          <Typography variant="body2" color="error">{t.actions.saveFailed}</Typography>
        ) : dirty ? (
          <Typography variant="body2" color="warning.main">{t.actions.unsaved}</Typography>
        ) : save.isSuccess ? (
          <Typography variant="body2" color="success.main">{t.actions.savedRestart}</Typography>
        ) : hasPersisted ? (
          <Typography variant="body2" color="text.secondary">{t.actions.allSaved}</Typography>
        ) : null}
      </Paper>

      {/* Guardrail: warn before persisting a changed connection that wasn't verified. */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs">
        <DialogTitle>{t.confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t.confirm.changedPrefix}{' '}
            <b>{(confirm ?? []).map((k) => t.confirm.sections[k] ?? k).join(', ')}</b>{' '}
            {(confirm ?? []).length > 1 ? t.confirm.notTestedThem : t.confirm.notTestedIt}
            <Box component="p" sx={{ mt: 1.5, mb: 0 }}>
              {t.confirm.warning}
            </Box>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="warning" onClick={() => { setConfirm(null); save.mutate(); }}>{t.confirm.saveAnyway}</Button>
          <Button variant="contained" onClick={() => setConfirm(null)}>{t.confirm.goBack}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function TestRow({ target, testing, result, onTest }: { target: string; testing: string | null; result?: { ok: boolean; message: string }; onTest: () => void }) {
  const t = useT().configuration;
  const busy = testing === target;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={busy ? <CircularProgress size={14} /> : <WifiTetheringIcon />}
        disabled={!!testing}
        onClick={onTest}
      >
        {busy ? t.testing : t.testConnection}
      </Button>
      {result && (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: result.ok ? 'success.main' : 'error.main', minWidth: 0 }}>
          {result.ok ? <CheckCircleIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
          <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{result.message}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

function Section({ icon, title, subtitle, children }: { icon: React.ReactNode; title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'primary.main', mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">{title}</Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
      <Divider sx={{ my: 1.5 }} />
      {children}
    </Paper>
  );
}
